import { api } from "../base";
import {
  accountSummariesToClstAccounts,
  accountSummariesToOptions,
  type SelectOption,
} from "./accountSummaries";
import type {
  AccountRole,
  ClstAccountStatus,
  IAccountSummary,
  IClstAccount,
} from "@pulse-ui/codegen-v2/generated_ts/clst_account";

export type {
  IAccountSummary,
  IClstAccount,
} from "@pulse-ui/codegen-v2/generated_ts/clst_account";

export interface GetAccountSummariesArgs {
  /** Omitted lists customer, house and otc accounts. */
  role?: `${AccountRole}`;
  /** Defaults to active on the server. */
  status?: `${ClstAccountStatus}` | "all";
}

export interface MarketAccountCapabilities {
  TransferOutgoingDest?: boolean;
}

export interface MarketAccount {
  Name: string;
  MarketAccountID?: number;
  SourceAccountID?: string | null;
  Market?: string | null;
  Ownership?: string | null;
  Status?: string | null;
  Type?: string | null;
  DisplayName?: string | null;
  Description?: string | null;
  Group?: string | null;
  Counterparty?: string | null;
  ReferenceData?: string | null;
  CredentialID?: number | null;
  Capabilities?: MarketAccountCapabilities | null;
}

export interface CustomerConfiguration {
  counterparty: string;
  all_in_tier: string | null;
  fee_tier: string | null;
  symbol_group: string | null;
  mode: string | null;
  update_user: string | null;
  revision: number | null;
}

interface RawCustomerConfiguration {
  Counterparty: string;
  AllInTier?: string | null;
  FeeTier?: string | null;
  SymbolGroup?: string | null;
  Mode?: string | null;
  UpdateUser?: string | null;
  Revision?: number | null;
}

export type { SelectOption } from "./accountSummaries";

export const NO_HOUSE_ACCOUNTS: SelectOption[] = [];

const accountManagerEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Customer account picker options; `name` filters client-side. */
    getAccountManagerAccounts: build.query<SelectOption[], { name?: string }>({
      query: () => ({ url: "/api/v1/accounts", params: { role: "customer" } }),
      transformResponse: (response: IAccountSummary[], _meta, { name }) =>
        accountSummariesToOptions(response, name),
      providesTags: ["AccountManagerAccounts", "AccountSummaries"],
    }),
    /** Active customer accounts in the legacy clst account shape. */
    getClstAccounts: build.query<IClstAccount[], void>({
      query: () => ({ url: "/api/v1/accounts", params: { role: "customer" } }),
      transformResponse: (response: IAccountSummary[]) =>
        accountSummariesToClstAccounts(response),
      providesTags: ["ClstAccounts", "AccountSummaries"],
    }),
    /** Fetches all customer configurations. */
    getCustomerConfigurations: build.query<CustomerConfiguration[], void>({
      query: () => ({ url: "/api/v1/customer-configurations" }),
      transformResponse: (
        response:
          | RawCustomerConfiguration[]
          | { data: RawCustomerConfiguration[] },
      ) => {
        const raw = Array.isArray(response) ? response : (response.data ?? []);
        return raw.map((c) => ({
          counterparty: c.Counterparty,
          all_in_tier: c.AllInTier ?? null,
          fee_tier: c.FeeTier ?? null,
          symbol_group: c.SymbolGroup ?? null,
          mode: c.Mode ?? null,
          update_user: c.UpdateUser ?? null,
          revision: c.Revision ?? null,
        }));
      },
      providesTags: ["CustomerConfigurations"],
    }),
    /** Fetches market account details from account-manager. */
    getMarketAccounts: build.query<MarketAccount[], void>({
      query: () => ({ url: "/api/v1/market-accounts" }),
      transformResponse: (
        response: MarketAccount[] | { data: MarketAccount[] },
      ) => (Array.isArray(response) ? response : (response.data ?? [])),
      providesTags: ["MarketAccounts"],
    }),
    /** Customer, house and OTC accounts in one shape, sorted by display name. */
    getAccountSummaries: build.query<
      IAccountSummary[],
      GetAccountSummariesArgs | void
    >({
      query: (args) => ({
        url: "/api/v1/accounts",
        params: { role: args?.role, status: args?.status },
      }),
      providesTags: ["AccountSummaries"],
    }),
    /** House account picker options; `name` filters client-side. */
    getHouseAccounts: build.query<SelectOption[], { name?: string }>({
      query: () => ({ url: "/api/v1/accounts", params: { role: "house" } }),
      transformResponse: (response: IAccountSummary[], _meta, { name }) =>
        accountSummariesToOptions(response, name),
      providesTags: ["HouseAccounts", "AccountSummaries"],
    }),
  }),
});

export const {
  useGetAccountManagerAccountsQuery,
  useGetClstAccountsQuery,
  useGetCustomerConfigurationsQuery,
  useGetMarketAccountsQuery,
  useGetHouseAccountsQuery,
  useGetAccountSummariesQuery,
} = accountManagerEndpoints;
